// Section 06 — Architecture facts, not performance metrics.
//
// Doctrinal posture (STRIKZA_Daily_Intelligence_Ledger.docx +
// DualSwarm_Architecture.docx + ACC v1.0): until the Ledger has
// 6+ months of sealed data, STRIKZA does NOT publish win-rate, CLV,
// active-user counts, or testimonials. The trust artifact is the
// ledger itself. Pre-launch, this section advertises the engine
// surface (agent count, pipeline count, market depth, audit cadence)
// — all verifiable from source — and an editorial commitment to
// audit-first proof.

interface BigStatProps {
  value: string;
  label: string;
  sub: string;
  suffix?: string;
}

function BigStat({ value, label, sub, suffix }: BigStatProps): JSX.Element {
  return (
    <div
      style={{
        padding: "44px 32px 36px",
        borderRight: "1px solid var(--border)",
        position: "relative",
      }}
    >
      <div
        className="display"
        style={{
          fontSize: "clamp(78px, 10vw, 152px)",
          color: "var(--text)",
          lineHeight: 0.85,
          letterSpacing: "0.005em",
        }}
      >
        {value}
        {suffix !== undefined && (
          <span style={{ color: "var(--accent)", fontSize: "0.55em", marginLeft: 4 }}>
            {suffix}
          </span>
        )}
      </div>
      <div
        className="mono"
        style={{
          fontSize: 11,
          letterSpacing: "0.18em",
          color: "var(--accent)",
          textTransform: "uppercase",
          marginTop: 18,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 6 }}>{sub}</div>
    </div>
  );
}

export function StatsSection(): JSX.Element {
  return (
    <section
      className="sz-l-section"
      style={{
        padding: "140px 0",
        borderTop: "1px solid var(--border)",
        background: "linear-gradient(180deg, #0C0B08 0%, #0A0A0A 100%)",
      }}
    >
      <div className="container">
        <div style={{ marginBottom: 60, textAlign: "center" }}>
          <span className="bracket" style={{ marginBottom: 24 }}>
            Section 06 · The engine surface
          </span>
          <h2
            className="serif"
            style={{
              marginTop: 24,
              fontSize: "clamp(40px, 4.5vw, 60px)",
              fontWeight: 500,
              color: "var(--text)",
              letterSpacing: "-0.01em",
            }}
          >
            Engine depth, not marketing claims.{" "}
            <em style={{ color: "var(--accent)", fontStyle: "italic" }}>The ledger speaks.</em>
          </h2>
        </div>
        <div
          className="sz-l-stats-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            border: "1px solid var(--border)",
            borderRight: 0,
            background: "var(--surface)",
          }}
        >
          <BigStat value="18" label="AI agents" sub="Two swarms · Claude + OpenAI" />
          <BigStat
            value="6"
            label="Pipelines"
            sub="Daily · POWERBALL · MEGA · LADDER · Singles · Owl"
          />
          <BigStat
            value="12"
            label="DNA profiles"
            sub="Manager · Player · Referee · Venue · Storyline · more"
          />
          <BigStat
            value="800"
            suffix="+"
            label="Markets per fixture"
            sub="Pre-filtered to 50 before any agent runs"
          />
        </div>

        {/* Quote strip */}
        <div
          className="sz-l-stats-quote"
          style={{
            marginTop: 4,
            padding: "44px 48px",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderTop: 0,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 40,
          }}
        >
          <div
            className="serif"
            style={{
              fontSize: 24,
              color: "var(--text)",
              fontStyle: "italic",
              lineHeight: 1.4,
              fontWeight: 400,
            }}
          >
            Every prediction. Sealed daily at 00:00 GMT. Within 4 months of launch, the STRIKZA
            Verified Return Certificate publishes a third-party-audited 90-day CLV+ record across at
            least 500 signals.{" "}
            <span style={{ color: "var(--accent)" }}>
              No win-rate boasts. The ledger does the talking.
            </span>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              className="mono"
              style={{
                fontSize: 12,
                letterSpacing: "0.1em",
                color: "var(--accent)",
                fontWeight: 500,
              }}
            >
              SIKA VERIFIED RETURN
            </div>
            <div
              className="mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.16em",
                color: "var(--text-muted)",
                marginTop: 4,
                textTransform: "uppercase",
              }}
            >
              Public · 4 months post-launch
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
